import BaseCard from '@/components/ui/Cards/BaseCard';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useCallback, useState, useEffect } from 'react';
import { useAddToLibraryMutation, useRemoveFromLibraryMutation } from '@/redux/api/client/library';
import useToast from '@/hooks/useToast';
import ShareModalCard from '@/components/ui/Cards/ShareModalCard';
import RemoveLibraryModalCard from '@/components/ui/Cards/RemoveLibraryModal';
import { ConfirmRemoveButtonToast } from '@/components/ui/Cards/ConfirmRemoveButtonToast';
import { SaveToLibraryToast } from '@/components/ui/Cards/SaveToLibraryToast';
import BaseCardList from '@/components/ui/Cards/BaseCardList';
import moreLikeThis from '@/utils/moreLikeThis';
import { useSelector } from 'react-redux';
import { selectPageLinkPaths } from '@/redux/selectors/pageSelectors';
import { EcommPanelParams } from '@/components/ui/SearchResults/hooks/useEcommData';
import EcommPanel from '@/components/ui/SearchResults/EcommPanel';
import { SearchableType } from '@/components/ui/SearchResults/searchableTypeToSnippetVariant';
import { CardType } from './constants';
import { useNavigate } from 'react-router-dom';
import { AnalyticsContext } from '@/analytics/constants';
import { ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { trackCardReadNowEvent } from '@/analytics/cards';
import {
  trackAddWishListEvent,
  trackRemoveWishListEvent,
  trackSelectItem
} from '@/analytics/ecommerce';
import { TDropdownMenuItem } from '../DropdownMenu';

enum Variant {
  Normal = 'normal',
  Retail = 'retail'
}

enum Size {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

interface PublicationCardProps {
  className?: string;
  cardClassName?: string;
  variant?: `${Variant}`;
  size?: `${Size}`;
  image?: {
    url: string;
    alt?: string;
  };
  labelIcon?: string;
  headingSmall?: string;
  heading?: string;
  labelDetail?: string;
  labelInfo?: string;
  href?: string;
  target?: string;
  onClickCard?: () => void;
  clickOnCardContent?: () => void;
  index?: number;
  showIndexOverlay?: boolean;
  variationPk?: string;
  isSaved?: boolean;
  heightAuto?: boolean;
  widthAuto?: boolean;
  showAsList?: boolean;
  productCode?: string | null;
  plusUrl?: string | null;
  searchableType?: string;
  hasPlusAccess?: boolean;
  context?: AnalyticsContext;
  guidValue: string | null;
  itemAnalyticsModel?: ItemAnalyticsModel;
  onRefreshParentData?: () => void;
  listId?: string;
  listName?: string;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
  cardTheme?: 'light' | 'default';
  balancedMediumLayout?: boolean;
  creditTrackerCode?: string;
  onOpenEcommPanel?: (params: EcommPanelParams) => void;
}

const PublicationCard: React.FC<PublicationCardProps> = ({
  className,
  cardClassName,
  cardTheme = 'default',
  variant = Variant.Normal,
  size = Size.Small,
  image,
  labelIcon,
  headingSmall,
  heading,
  labelDetail,
  labelInfo,
  href,
  target,
  onClickCard,
  clickOnCardContent,
  index,
  showIndexOverlay,
  variationPk,
  isSaved,
  heightAuto,
  widthAuto,
  showAsList,
  productCode,
  plusUrl,
  searchableType,
  hasPlusAccess,
  context,
  guidValue,
  itemAnalyticsModel,
  onRefreshParentData,
  listId,
  listName,
  itemListAnalyticsModel,
  balancedMediumLayout,
  creditTrackerCode,
  onOpenEcommPanel
}) => {
  const pageLinkPaths = useSelector(selectPageLinkPaths);
  const searchPageUrl = pageLinkPaths?.Search;

  const thumbnailIcon = variant === Variant.Retail ? 'cart' : 'link-out';
  const [isOpenShareModal, setIsOpenShareModal] = useState(false);
  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState(false);
  const [openEcom, setOpenEcom] = useState(false);
  const [addToLibrary] = useAddToLibraryMutation();
  const [removeFromLibrary] = useRemoveFromLibraryMutation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  let params: EcommPanelParams | undefined;

  const belowLg = useMediaQuery(ScreenSizeQueries.belowLg);
  const navigate = useNavigate();
  const analyticsContext = context ?? AnalyticsContext.Cards;
  let buttonOptionsVariation;

  const shareButtonFunction = () => {
    setIsOpenShareModal(!isOpenShareModal);
  };

  const removeButtonFunction = () => {
    setIsOpenRemoveModal(!isOpenRemoveModal);
  };

  const removeFromLibraryQuery = useCallback(
    async (pk: string, undoAction?: boolean) => {
      trackRemoveWishListEvent(itemAnalyticsModel, undefined, false, itemListAnalyticsModel);
      const dataResponse = await removeFromLibrary(pk);
      if ('error' in dataResponse) {
        return { error: true };
      } else if ('data' in dataResponse && undoAction) {
        setIsSavedToLibrary(false);
        if (onRefreshParentData) onRefreshParentData();
        return showSuccessMessage('Item Removed from library');
      } else if ('data' in dataResponse) {
        setIsSavedToLibrary(false);
        if (onRefreshParentData) onRefreshParentData();
        return { data: true };
      }
    },
    [
      itemAnalyticsModel,
      itemListAnalyticsModel,
      removeFromLibrary,
      onRefreshParentData,
      showSuccessMessage
    ]
  );

  const addToLibraryQuery = useCallback(
    async (pk: string, undoAction?: boolean) => {
      const dataResponse = await addToLibrary(pk);

      if ('error' in dataResponse) {
        return { error: true };
      } else if ('data' in dataResponse && undoAction) {
        setIsSavedToLibrary(true);
        if (onRefreshParentData) onRefreshParentData();
        return showSuccessMessage('Item Added to library');
      } else if ('data' in dataResponse) {
        setIsSavedToLibrary(true);
        return { data: true };
      }
    },
    [addToLibrary, onRefreshParentData, showSuccessMessage]
  );

  const confirmRemoveButtonFunction = useCallback(
    async (pk: string) => {
      const onClickFunction = () => {
        addToLibraryQuery(variationPk!, true);
      };

      const toastNode = (
        <ConfirmRemoveButtonToast
          message="Thanks for the feedback, the program has been removed. "
          onClickFunction={onClickFunction}
        />
      );

      const removeResponse = await removeFromLibraryQuery(pk);

      if (removeResponse && 'error' in removeResponse) {
        showFailMessage('Error while removing item to library');
      } else if (removeResponse && 'data' in removeResponse) {
        setIsSavedToLibrary(false);
        showSuccessMessage(toastNode);
      }
    },
    [addToLibraryQuery, removeFromLibraryQuery, showSuccessMessage, showFailMessage, variationPk]
  );

  const saveToLibraryFunction = useCallback(
    async (pk: string) => {
      const onClickFunction = () => {
        removeFromLibraryQuery(variationPk!, true);
      };

      const toastNode = (
        <SaveToLibraryToast message="Saved item to library. " onClickFunction={onClickFunction} />
      );

      trackSelectItem(
        itemAnalyticsModel,
        analyticsContext,
        listId,
        listName,
        itemListAnalyticsModel,
        typeof index === 'number' && index >= 0 ? index : undefined
      );
      trackAddWishListEvent(
        itemAnalyticsModel,
        analyticsContext,
        itemListAnalyticsModel,
        typeof index === 'number' && index >= 0 ? index : undefined
      );

      const addResponse = await addToLibraryQuery(pk);

      if (addResponse && 'error' in addResponse) {
        showFailMessage('Error while adding item to library');
      } else if (addResponse && 'data' in addResponse) {
        setIsSavedToLibrary(true);
        showSuccessMessage(toastNode);
      }
    },
    [
      itemAnalyticsModel,
      analyticsContext,
      itemListAnalyticsModel,
      listId,
      listName,
      index,
      addToLibraryQuery,
      removeFromLibraryQuery,
      variationPk,
      showFailMessage,
      showSuccessMessage
    ]
  );

  const shareButtonOption = { label: 'Share', onClick: shareButtonFunction, icon: 'share' };

  const removeFromLibraryOption = {
    label: 'Remove from library',
    onClick: removeButtonFunction,
    icon: 'delete'
  };

  const saveToLibraryOption = {
    label: 'Save to my library',
    onClick: () => saveToLibraryFunction(variationPk!),
    icon: 'add'
  };

  useEffect(() => {
    setIsSavedToLibrary(Boolean(isSaved));
  }, [isSaved]);
  const saveToLibrary = isSavedToLibrary ? removeFromLibraryOption : saveToLibraryOption;

  const buttonOptions: TDropdownMenuItem[] = [
    saveToLibrary,
    shareButtonOption,
    { separator: true },
    {
      label: 'Show more like this',
      onClick: () => {
        moreLikeThis(navigate, productCode, searchPageUrl, itemAnalyticsModel);
      },
      icon: 'search'
    }
  ];

  const thumbnailProps = {
    image,
    iconThumbnail: {
      name: thumbnailIcon,
      label: labelIcon
    }
  };

  if (belowLg) {
    buttonOptionsVariation = [
      {
        label: labelIcon,
        onClick: () => {
          cardProps.onClickCard && cardProps.onClickCard();
        },
        icon: thumbnailIcon
      },
      ...(buttonOptions ?? [])
    ];
  } else {
    buttonOptionsVariation = buttonOptions ?? [];
  }

  let cardProps = {
    className,
    cardClassName,
    containerClass:
      cardTheme === 'light' ? 'bg-white min-h-[180px] sm:min-h-0' : 'min-h-[180px] sm:min-h-0',
    size,
    headingSmall,
    heading,
    labelDetail,
    labelInfo,
    href,
    target,
    onClickCard,
    clickOnCardContent,
    index,
    showIndexOverlay,
    heightAuto,
    widthAuto,
    balancedMediumLayout,
    cardType: CardType.Publication,
    cardTheme
  };

  cardProps = {
    ...cardProps,
    onClickCard: () => {
      trackSelectItem(
        itemAnalyticsModel,
        analyticsContext,
        listId,
        listName,
        itemListAnalyticsModel,
        typeof index === 'number' && index >= 0 ? index : undefined
      );
      if (hasPlusAccess && plusUrl) {
        trackCardReadNowEvent(itemAnalyticsModel);
        window.open(plusUrl, '_blank');
        return;
      }

      if (params && onOpenEcommPanel) onOpenEcommPanel(params);
      else setOpenEcom(true);
    },
    clickOnCardContent: () => {
      trackSelectItem(
        itemAnalyticsModel,
        analyticsContext,
        listId,
        listName,
        itemListAnalyticsModel,
        typeof index === 'number' && index >= 0 ? index : undefined
      );
    }
  };

  if (searchableType) {
    params = {
      pk: variationPk!,
      url: href!,
      title: heading!,
      searchableType: searchableType as SearchableType,
      action: 'BuyNow',
      guidValue,
      creditTrackerCode,
      itemAnalyticsModel,
      itemListAnalyticsModel
    };
  }

  return (
    <>
      {showAsList ? (
        <BaseCardList
          buttonOptions={buttonOptionsVariation}
          thumbnailProps={thumbnailProps}
          {...cardProps}
        />
      ) : (
        <BaseCard
          thumbnailProps={thumbnailProps}
          buttonOptions={buttonOptionsVariation}
          {...cardProps}
        />
      )}

      <ShareModalCard
        modalHeader="Share"
        pathShareLink={`/product/${variationPk}`}
        programTitle={cardProps.heading!}
        isOpen={isOpenShareModal}
        cardOpenModal={setIsOpenShareModal}
        itemAnalyticsModel={itemAnalyticsModel}
        context={analyticsContext}
      />
      <RemoveLibraryModalCard
        modalHeader="Do you want to remove this program from your Library?"
        isOpen={isOpenRemoveModal}
        onSaveButtonPress={() => confirmRemoveButtonFunction(variationPk!)}
        cardOpenModal={setIsOpenRemoveModal}
      />
      {!onOpenEcommPanel && openEcom && params && (
        <EcommPanel params={params} onClose={() => setOpenEcom(false)} context={analyticsContext} />
      )}
    </>
  );
};

export default PublicationCard;
